import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { Body } from '@iris/react-components/Body';
import { Button as IrisButton } from '@iris/react-components/Button';
import clsx from 'clsx';

import { Button, LinkIcon } from 'reaxl';
import { sendClick, valueEventCode } from 'reaxl-analytics';
import { ndsLendingOptionsCTAClick } from 'reaxl-analytics-handlers';

import contentAnalyticsHandlers from '@/analytics/contentAnalyticsHandlers';

import CardSkeleton from '@/components/CardSkeleton';

import WestlakeButton from './components/WestlakeButton';
import WestlakeLogoIcon from './components/WestlakeLogoIcon';
import getWestlakeApiObject from './utilities';

const WestlakeCalculatorLink = React.memo(({ linkClassName, uiContext, onWestlakeClick }) => (
    <LinkIcon
        data-cmp="westlake-prequal-cta"
        key="westlakeLink"
        className={linkClassName}
        glyph={uiContext === 'calculator' ? 'chevron-right' : ''}
        onClick={onWestlakeClick}
        undecorated
    >
        Get Pre-Qualified
    </LinkIcon>
));

const WestlakePriceSection = React.memo(({ onWestlakeClick }) => (
    <div className="display-flex gap-4 align-items-start">
        <div className="display-flex flex-shrink-0 padding-top-1">
            <WestlakeLogoIcon />
        </div>
        <Body
            className="flex-1"
            size="sm"
            as="div"
        >
            <Body
                className="margin-bottom-1 iris:font-bold!"
                size="sm"
                as="p"
            >
                See Your Estimated Monthly Payments
            </Body>
            Know your financing options before visiting the dealership.
            {' '}
            <IrisButton
                onClick={onWestlakeClick}
                size="sm"
                variant="unstyled"
                className="iris:underline iris:font-normal iris:text-[var(--base-color-primary-600)]"
            >
                Explore financing
            </IrisButton>
        </Body>
    </div>
));

const WestlakeDefaultButton = React.memo(({ onWestlakeClick }) => (
    <Button
        data-cmp="westlake-prequal-cta"
        key="westlakeButton"
        className="padding-horizontal-0"
        block
        onClick={onWestlakeClick}
    >
        Get Pre-Qualified
    </Button>
));

const WestlakeButtonVarient = React.memo(({ className, onWestlakeClick, personalization, listing, user, rest }) => (
    <div
        className={clsx('westlake-prequalification-button', className)}
        data-cmp="westlake-prequal-cta"
        key="westlakeButton"
        {...rest}
    >
        <WestlakeButton
            onClick={onWestlakeClick}
            personalization={personalization}
            listing={listing}
            user={user}
        />
    </div>
));

/**
 * Westlake - Renders Westlake Financial pre-qualification integration component
 *
 * This component provides multiple UI variants for Westlake Financial pre-qualification
 * including buttons, links, and calculator interfaces. It manages iframe communication,
 * script injection monitoring, modal interactions, and comprehensive analytics tracking.
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes for the wrapper element
 * @param {string} [props.linkClassName] - CSS classes for link variants
 * @param {Object} [props.listing={}] - Vehicle listing data
 * @param {boolean} [props.listing.isWestlakePrequal=false] - Whether Westlake pre-qual is enabled for this listing
 * @param {string} [props.listing.id] - Unique listing identifier for analytics
 * @param {Function} [props.onWestlakeButtonClick] - Callback function when Westlake CTA is clicked
 * @param {string} [props.westlakePreQualUrl=''] - Base URL for Westlake pre-qualification API
 * @param {string} [props.page] - Current page identifier for analytics
 * @param {string} [props.placement] - Placement context (nds, nba, price-section, top)
 * @param {Object} [props.personalization] - Personalization data for retargeting
 * @param {string} [props.uiContext='button'] - UI variant to render
 * @param {Object} [props.user] - User data for personalization
 * @param {Function} [props.onError] - Error callback when script fails to load
 *
 */
function Westlake({
    className,
    linkClassName,
    listing = {},
    onWestlakeButtonClick = () => { },
    westlakePreQualUrl = '',
    page,
    placement,
    personalization,
    uiContext = 'button',
    user,
    onError = () => { },
    ...rest
}) {
    const { isWestlakePrequal = false } = listing;

    const [scriptInjected, setScriptInjected] = useState(false);

    const westlakeIframe = useRef();
    const countRef = useRef(0);
    const intervalRef = useRef(null);
    const isPriceSection = uiContext === 'price-section';

    // Setup iFrame environment on first load / SPA transition
    useEffect(() => {
        if (isWestlakePrequal) {
            // Setup event listeners
            const messageLog = [];
            const westlakeMessageListener = (e) => {
                // Pass on messages to iframe that are sent to global window by Westlake script
                if (e?.origin.includes('autotrader.carzing.com') && !messageLog.includes(e?.timeStamp)) {
                    messageLog.push(e.timeStamp);
                    westlakeIframe.current.contentWindow.postMessage(e.data, '*');
                }

                // Handle events for modal interactions
                if (e?.data?.type === 'WESTLAKE_MODAL_OPENED') {
                    westlakeIframe.current.style.opacity = '1';
                    westlakeIframe.current.style.left = '0';
                }
                if (e?.data?.type === 'WESTLAKE_MODAL_CLOSED') {
                    westlakeIframe.current.style.opacity = '0';
                    westlakeIframe.current.style.left = '-100vw';
                }
            };
            window.addEventListener('message', westlakeMessageListener);

            // Get ref for iFrame
            if (!westlakeIframe.current) {
                let westlakeIframeDOM = document.getElementById('westlakeIntegration');
                if (!westlakeIframeDOM) {
                    westlakeIframeDOM = document.createElement('iframe');
                    westlakeIframeDOM.setAttribute('id', 'westlakeIntegration');
                    westlakeIframeDOM.setAttribute('title', 'Westlake Financing: Get Prequalified');
                    westlakeIframeDOM.setAttribute('src', `/cars-for-sale/assets/atc/westlake.html?url=${westlakePreQualUrl}`);
                    westlakeIframeDOM.style.width = '100vw';
                    westlakeIframeDOM.style.height = '100vh';
                    westlakeIframeDOM.style.opacity = '0';
                    westlakeIframeDOM.style.position = 'fixed';
                    westlakeIframeDOM.style.top = '0';
                    westlakeIframeDOM.style.left = '-100vw';
                    westlakeIframeDOM.style.zIndex = 2147483646;
                    westlakeIframeDOM.classList.add('border-0');
                    document.body.appendChild(westlakeIframeDOM);
                }
                westlakeIframe.current = westlakeIframeDOM;
            }

            // Wait for carzing script to load
            const monitorScriptLoaded = () => {
                try {
                    if (westlakeIframe?.current?.contentWindow?.carzing) {
                        setScriptInjected(true);
                        return true;
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.log(error);
                }
                return false;
            };

            intervalRef.current = setInterval(() => {
                countRef.current += 1;

                if (scriptInjected || monitorScriptLoaded() || countRef.current > 15) {
                    clearInterval(intervalRef.current);
                }

                if (countRef.current > 15) {
                    onError();
                }
            }, 200);

            return () => {
                window.removeEventListener('message', westlakeMessageListener);

                if (intervalRef?.current) {
                    clearInterval(intervalRef.current);
                }
            };
        }

        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isWestlakePrequal, listing.id]);

    if (!isWestlakePrequal || (!scriptInjected && !isPriceSection)) {
        return null;
    }

    if (!scriptInjected && isPriceSection) {
        return <CardSkeleton />;
    }

    const openWestlakePrequalModal = () => {
        try {
            // named for debugger visibility
            const init = getWestlakeApiObject(listing);
            westlakeIframe.current.contentWindow.postMessage({ type: 'WESTLAKE_INIT', payload: init }, '*');
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
        }
    };

    const onWestlakeClick = (event) => {
        event.stopPropagation();
        onWestlakeButtonClick();
        const isGetPreQualified = ['link', 'calculator', 'default-button'].includes(uiContext);

        const data = {
            epn: 'Westlake Financing',
            pixallData: {
                applicationType: 'loan',
                eventSource: 'application',
                feature: 'caca',
                label: `get_loan-${['nds', 'nba', 'price-card'].includes(placement) ? placement.replace(/-/g, '') : 'top'}`,
                vendor: 'westlake',
                cdl: {
                    eventProperties: {
                        event: {
                            valueEventCode: isGetPreQualified ? valueEventCode.CREDIT_APP : valueEventCode.WESTLAKE,
                        },
                    },
                },
            },
            placement,
            uiContext,
        };

        if (['button', 'link', 'calculator', 'default-button', 'styled', 'simplified', 'price-section'].includes(uiContext)) {
            sendClick(contentAnalyticsHandlers.preQualCTAClick, event, data);
        } else {
            sendClick(ndsLendingOptionsCTAClick, event, {
                listingId: listing?.id,
                placement,
                uiContext,
                vendor: 'westlake',
                isWestlakeEnabled: true,
            });
        }

        // Trigger Westlake modal
        if (scriptInjected) {
            openWestlakePrequalModal();
        }
    };

    switch (uiContext) {
        case 'link':
        case 'calculator':
            return (
                <WestlakeCalculatorLink
                    linkClassName={linkClassName}
                    uiContext={uiContext}
                    onWestlakeClick={onWestlakeClick}
                />
            );
        case 'price-section':
            return <WestlakePriceSection onWestlakeClick={onWestlakeClick} />;
        case 'default-button':
            return <WestlakeDefaultButton onWestlakeClick={onWestlakeClick} />;
        case 'button':
        default:
            return (
                <WestlakeButtonVarient
                    className={className}
                    onWestlakeClick={onWestlakeClick}
                    personalization={personalization}
                    listing={listing}
                    user={user}
                    rest={rest}
                />
            );
    }
}

Westlake.propTypes = {
    /**
     * Additional CSS classes for the wrapper element
     */
    className: PropTypes.string,
    /**
     * CSS classes for link variants
     */
    linkClassName: PropTypes.string,
    /**
     * Vehicle listing data containing Westlake eligibility and identification
     */
    listing: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isWestlakePrequal: PropTypes.bool,
    }),
    /**
     * Current page identifier for analytics tracking
     */
    page: PropTypes.string,
    /**
     * Placement context for analytics (nds, nba, price-section, top)
     */
    placement: PropTypes.oneOf(['nds', 'nba', 'price-card', 'top']),
    /**
     * Personalization data object for retargeting and customization
     */
    personalization: PropTypes.object,
    /**
     * User data object for personalization features
     */
    user: PropTypes.object,
    /**
     * Callback function executed when Westlake CTA is clicked
     */
    onWestlakeButtonClick: PropTypes.func,
    /**
     * Base URL for Westlake pre-qualification API integration
     */
    westlakePreQualUrl: PropTypes.string,
    /**
     * UI context that determines the type of CTA component to render
     */
    uiContext: PropTypes.oneOf(['button', 'link', 'calculator', 'price-section', 'default-button', 'styled', 'simplified', 'section']),
    /**
     * Error callback function executed when script fails to load
     */
    onError: PropTypes.func,
};

export default Westlake;
